import { useState, useEffect } from 'react';
import { Play, Star, Users, Award, Clock } from 'lucide-react';
import Button from '../base/Button';

export default function HeroSection() {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const slides = [
    {
      title: "La Magie de Dubaï",
      subtitle: "Votre Aventure Commence Ici",
      description: "Basée à Dubaï depuis plus de 10 ans, notre équipe française vous accompagne pour créer des souvenirs inoubliables. Safari dans le désert, gratte-ciels iconiques, expériences de luxe.",
      image: "/images/hero/hero1.jpg",
      cta: "Découvrir nos activités"
    },
    {
      title: "Safari dans le Désert",
      subtitle: "Aventure Authentique",
      description: "Vivez l'expérience ultime du désert avec nos safaris en 4x4, dîner bédouin sous les étoiles, et spectacles traditionnels. Une aventure inoubliable vous attend.",
      image: "/images/hero/hero2.jpg",
      cta: "Réserver un safari"
    },
    {
      title: "Hôtels de Luxe",
      subtitle: "Séjour d'Exception",
      description: "Sélection exclusive d'hôtels 5 étoiles, resorts de luxe sur Palm Jumeirah, et expériences VIP. Votre confort est notre priorité.",
      image: "/images/hero/hero3.jpg",
      cta: "Voir nos hôtels"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const trustIndicators = [
    { icon: Star, text: "Note 4.9/5", subtext: "2000+ avis" },
    { icon: Users, text: "+100k clients", subtext: "satisfaits" },
    { icon: Award, text: "Certifié", subtext: "Qualité Dubai" },
    { icon: Clock, text: "Support 7j/7", subtext: "Équipe française" }
  ];

  return (
    <section className="relative h-screen overflow-hidden">
      {/* Background Slider */}
      <div className="absolute inset-0">
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={slide.image}
              alt={slide.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent"></div>
          </div>
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex items-start pt-20">
        <div className="max-w-7xl mx-auto px-4 w-full">
          <div className="max-w-3xl">
            {/* Trust badges */}
            <div className="flex flex-wrap gap-4 mb-6">
              {trustIndicators.map((indicator, index) => (
                <div key={index} className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 text-white">
                  <indicator.icon size={20} className="text-amber-400" />
                  <div className="text-sm">
                    <div className="font-semibold">{indicator.text}</div>
                    <div className="text-xs opacity-80">{indicator.subtext}</div>
                  </div>
                </div>
              ))}
            </div>

            <h1 className="text-5xl lg:text-7xl font-bold text-white mb-4 leading-tight">
              {slides[currentSlide].title}
            </h1>
            
            <h2 className="text-2xl lg:text-3xl text-amber-400 font-semibold mb-6">
              {slides[currentSlide].subtitle}
            </h2>
            
            <p className="text-xl text-white/90 mb-8 leading-relaxed max-w-2xl">
              {slides[currentSlide].description}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Button size="lg" className="text-lg px-8 py-4">
                {slides[currentSlide].cta}
              </Button>
              <button className="flex items-center space-x-3 text-white hover:text-amber-400 transition-colors group">
                <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:bg-amber-400/20 transition-colors">
                  <Play size={20} fill="currentColor" />
                </div>
                <span className="text-lg font-medium">Voir la vidéo</span>
              </button>
            </div>

            {/* Quick actions */}
            <div className="flex flex-wrap gap-3">
              <a href="#activites" className="bg-white/10 backdrop-blur-sm text-white px-4 py-2 rounded-full hover:bg-white/20 transition-colors">
                🏜️ Safari désert
              </a>
              <a href="#hotels" className="bg-white/10 backdrop-blur-sm text-white px-4 py-2 rounded-full hover:bg-white/20 transition-colors">
                🏨 Hôtels luxe
              </a>
              <a href="#burj-khalifa" className="bg-white/10 backdrop-blur-sm text-white px-4 py-2 rounded-full hover:bg-white/20 transition-colors">
                🏗️ Burj Khalifa
              </a>
              <a href="#parcs" className="bg-white/10 backdrop-blur-sm text-white px-4 py-2 rounded-full hover:bg-white/20 transition-colors">
                🎢 Parcs aquatiques
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Slide indicators */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-3 z-20">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${
              index === currentSlide ? 'bg-amber-400 w-8' : 'bg-white/50'
            }`}
          />
        ))}
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 right-8 text-white animate-bounce">
        <div className="flex flex-col items-center space-y-2">
          <span className="text-sm">Découvrir</span>
          <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white rounded-full mt-2 animate-pulse"></div>
          </div>
        </div>
      </div>
    </section>
  );
}